import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlay } from '../contexts/PlayContext';
import { games } from '../data/games';
import { parseScoreType, secondsToTimeString, timeStringToSeconds } from '../utils/scoreUtils';

// Add Google Fonts CSS for Google Sans Code
const googleFontsLink = document.createElement('link');
googleFontsLink.href = 'https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@700&display=swap';
googleFontsLink.rel = 'stylesheet';
if (!document.head.querySelector(`link[href="${googleFontsLink.href}"]`)) {
    document.head.appendChild(googleFontsLink);
}

// Unified Numeric Score Entry Component
const NumericScoreInput = ({ scoreType, digits, value, onChange }) => {
    const isTime = scoreType === 'time';
    
    // Handle digit click based on score type
    const handleDigitClick = (digit) => {
        if (isTime) {
            // Time logic: Clear X if present, then work with MM:SS format
            const timeValue = value === 'X' ? "00:00" : (value || "00:00");
            const [minutes, seconds] = timeValue.split(':').map(v => v.padStart(2, '0'));
            const currentDigits = (minutes + seconds).replace(/^0+/, '') || '0';
            const newDigits = (currentDigits + digit.toString()).slice(-4).padStart(4, '0');
            
            const newMinutes = newDigits.slice(0, 2);
            const newSeconds = newDigits.slice(2, 4);
            onChange(`${newMinutes}:${newSeconds}`);
        } else {
            // Int logic: Clear X if present, then work with fixed number of digits
            const currentValue = value === 'X' ? '' : value;
            const newValue = (currentValue + digit.toString()).slice(-digits);
            onChange(newValue);
        }
    };
    
    const handleClear = () => {
        if (isTime) {
            onChange("00:00");
        } else {
            onChange('');
        }
    };
    
    const handleXClick = () => {
        onChange('X');
    };
    
    // Render display content based on score type
    const renderDisplay = () => {
        // If value is 'X', show just X for both types
        if (value === 'X') {
            return <span className="text-black">X</span>;
        }
        
        if (isTime) {
            const timeValue = value || "00:00";
            const [minutes, seconds] = timeValue.split(':').map(v => v.padStart(2, '0'));
            
            // Determine which digits are placeholders (leading zeros)
            const allDigits = minutes + seconds;
            const nonZeroStart = allDigits.search(/[^0]/);
            const hasNonZero = nonZeroStart !== -1;
            
            const renderDigit = (digit, index) => {
                const isPlaceholder = !hasNonZero || (hasNonZero && nonZeroStart > index);
                return (
                    <span key={index} className={isPlaceholder ? 'text-gray-300' : 'text-black'}>
                        {digit}
                    </span>
                );
            };
            
            return (
                <>
                    {renderDigit(minutes[0], 0)}
                    {renderDigit(minutes[1], 1)}
                    <span className="text-black">:</span>
                    {renderDigit(seconds[0], 2)}
                    {renderDigit(seconds[1], 3)}
                </>
            );
        } else {
            // Int display with greyed out zero placeholders
            const displayValue = value || '';
            const paddedValue = displayValue.padStart(digits, '0');
            const nonZeroStart = paddedValue.search(/[^0]/);
            const hasNonZero = nonZeroStart !== -1;
            
            // Special case: if value is explicitly "0", show rightmost digit in black
            const isExplicitZero = value==='0' || value==='00' || value==='000';
            
            return paddedValue.split('').map((digit, index) => {
                // If explicitly "0", show the rightmost digit in black, rest in gray
                if (isExplicitZero) {
                    const isRightmost = index === paddedValue.length - 1;
                    return (
                        <span key={index} className={isRightmost ? 'text-black' : 'text-gray-300'}>
                            {digit}
                        </span>
                    );
                }
                
                // Otherwise, use normal placeholder logic
                const isPlaceholder = !hasNonZero || (hasNonZero && index < nonZeroStart);
                return (
                    <span key={index} className={isPlaceholder ? 'text-gray-300' : 'text-black'}>
                        {digit}
                    </span>
                );
            });
        }
    };
    
    return (
        <div className="flex flex-col items-center space-y-4">
            {/* Display */}
            <div className="flex justify-center">
                <div
                    className="bg-white border-2 border-blue-300 rounded-lg px-2 py-1 text-center text-5xl font-bold"
                    style={{
                        fontFamily: "'Source Code Pro', monospace",
                        minWidth: isTime ? '10rem' : `${digits * 2.5}rem`,
                        height: '4rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {renderDisplay()}
                </div>
            </div>
            
            {/* Number pad with X and Clear buttons */}
            <div className="flex items-center gap-1">
                {/* X button on the left (for both int and time types) */}
                <button
                    onClick={handleXClick}
                    className="w-9 h-9 xs:w-12 xs:h-12 bg-yellow-400 border-2 border-blue-800 text-blue-800 rounded-full font-bold text-lg xs:text-xl transition-colors duration-200 -mr-1"
                    type="button"
                    title="Failed puzzle"
                >
                    X
                </button>
                
                {/* Number pad */}
                <div className="grid grid-cols-5 gap-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((digit) => (
                        <button
                            key={digit}
                            onClick={() => handleDigitClick(digit)}
                            className="w-9 h-9 xs:w-12 xs:h-12 bg-yellow-400 border-2 border-blue-800 text-blue-800 rounded-full font-bold text-base xs:text-lg transition-colors duration-200"
                            type="button"
                        >
                            {digit}
                        </button>
                    ))}
                </div>
                
                {/* Clear button on the right */}
                <button
                    onClick={handleClear}
                    className="w-9 h-9 xs:w-12 xs:h-12 bg-yellow-400 border-2 border-blue-800 text-blue-800 rounded-full transition-colors duration-200 flex items-center justify-center -ml-1"
                    type="button"
                    title="Clear"
                >
                    <svg className="w-3.5 h-3.5 xs:w-[18px] xs:h-[18px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 14 L4 9 L9 4" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4 9 L14 9 C17.866 9 21 12.134 21 16 C21 19.866 17.866 23 14 23 L12 23" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>
        </div>
    );
};

function EditScoreForm() {
    const { gameId } = useParams();
    const navigate = useNavigate();
    const [score, setScore] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [game, setGame] = useState(null);
    const [scoreTypeInfo, setScoreTypeInfo] = useState(null);

    const { user } = useAuth();
    const { updatePlay, getGamePlayForToday, clearRecentGameVisit, loadFriendsData } = usePlay();

    useEffect(() => {
        // No account needed to score any more (D22) - a visitor with no record
        // yet just has no existing play to prefill from.
        if (!gameId) return;

        // Find game
        const foundGame = games.find(g => g.id.toString() === gameId);
        console.log("score for:",foundGame);
        setGame(foundGame);
        
        // Null for a game with no scoreType, and for one whose type this form
        // has no keypad for (`bool`) - both mean "nothing numeric to prefill".
        const parsedType = foundGame?.scoreType ? parseScoreType(foundGame.scoreType) : null;
        setScoreTypeInfo(parsedType);

        // Load friends data first, then load existing score
        const loadDataAndScore = async () => {
            try {
                // Ensure the dashboard is loaded (needed for getGamePlayForToday).
                // Skipped with no record - there's nothing to fetch yet.
                if (user) await loadFriendsData();

                // A play row is not a score: one is written on every game-link
                // click, so arriving here from a game always finds a row with a
                // null score. Prefill only from a real score, or the form opens
                // showing a result the user never entered.
                const existingPlay = getGamePlayForToday(gameId);
                const hasScore = !!existingPlay && existingPlay.score !== null && existingPlay.score !== '';
                const isTimeScore = parsedType && parsedType.type === 'time';

                if (hasScore) {
                    setScore(isTimeScore ? secondsToTimeString(existingPlay.score) : existingPlay.score);
                } else {
                    setScore(isTimeScore ? "00:00" : "");
                }
                setMessage(existingPlay?.message || "");
            } catch (error) {
                console.error('Error loading existing score:', error);
            }
        };

        loadDataAndScore();
    }, [user, gameId, getGamePlayForToday, loadFriendsData]);

    const submit = async () => {
        if (!score.trim()) {
            setError("Please enter a score");
            return;
        }

        setLoading(true);
        setError("");

        try {
            // Convert score based on type before submitting
            let scoreToSubmit;
            if (scoreTypeInfo && scoreTypeInfo.type === 'time') {
                // Convert MM:SS format to integer seconds
                scoreToSubmit = timeStringToSeconds(score);
            } else {
                // Strip leading zeros from score before submitting
                scoreToSubmit = score.trim().replace(/^0+/, '') || '0';
            }
            
            await updatePlay(gameId*1, scoreToSubmit+"", message.trim());

            // Clear recent game visit data since score has been entered
            clearRecentGameVisit();

            // Always back to Home, whichever way you got here (4.8). Home is
            // where the score you just logged actually shows up.
            navigate('/');
        } catch (error) {
            console.log('Error updating score:', error);
            setError("Failed to save score. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const cancel = () => {
        // Clear recent game visit data when user cancels
        clearRecentGameVisit();

        // Same destination as a successful save - see above.
        navigate('/');
    };

    if (!gameId) {
        return (
            <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
                <div className="text-yellow-600">Game not found</div>
            </div>
        );
    }

    return (
        <section className="max-w-6xl mx-auto p-2 sm:p-3 relative z-10">
            <div className="max-w-md mx-auto bg-blue-100 dark:bg-black rounded-lg p-2">
            
            {/* Game display with image and name */}
            {game && (
                <div className="my-3 flex items-center justify-center">
                    <img
                        src={`/img/games/${game.image}`}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg border-2 border-blue-800 mr-2 flex-shrink-0 shadow-sm"
                        alt={game.name}
                    />
                    <p className="font-bold text-left text-base xs:text-2xl text-blue-800 dark:text-white dark:font-normal">{game.name}</p>
                </div>
            )}

            {error && (
                <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-lg">
                    {error}
                </div>
            )}

            <div className="space-y-4">
                <div>
                    <label className="block text-blue-800 dark:text-blue-200 font-medium mb-4 text-center">
                        {game && game.scorePrompt && game.scorePrompt.trim() ? game.scorePrompt : 'Score'}
                        <p className="text-sm pt-1 opacity-50">...or enter <b>X</b> if you failed today's challenge</p>
                    </label>
                    
                    {/* Render numeric score entry component based on scoreType */}
                    {scoreTypeInfo && (scoreTypeInfo.type === 'int' || scoreTypeInfo.type === 'time') && (
                        <NumericScoreInput
                            scoreType={scoreTypeInfo.type}
                            digits={scoreTypeInfo.digits || 4}
                            value={score}
                            onChange={setScore}
                        />
                    )}
                    
                </div>

                <div>
                    <input
                        id="message-field"
                        type="text"
                        placeholder="Add a comment (optional)"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800"
                        disabled={loading}
                    />
                </div>

                <div className="flex space-x-3 pt-2">
                    <button
                        onClick={submit}
                        disabled={loading}
                        className="flex-1 bg-yellow-400 border-2 border-blue-800 text-blue-800 py-2 px-4 rounded-lg font-bold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Saving...' : 'Save Score'}
                    </button>
                    <button
                        onClick={cancel}
                        disabled={loading}
                        className="flex-1 bg-gray-500 dark:bg-gray-700 text-white py-2 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancel
                    </button>
                </div>

                {game && game.url && (
                    <div className="text-center pb-2">
                        <a
                            href={game.url}
                            className="text-sm text-blue-800 dark:text-blue-300 underline opacity-70 hover:opacity-100"
                        >
                            Return to {game.name}
                        </a>
                    </div>
                )}
            </div>
            </div>
        </section>
    );
}

export default EditScoreForm;



